import useFuzzy from '@/core/hooks/useFuzzy/useFuzzy'
import { DataFeedSource, EndState } from '@atbay/technology-service-node-sdk'
import { JobsResponseDataJob } from '@atbay/technology-service-node-sdk/lib/types/models/JobsResponseDataJob'
import { BaseModule } from '@core/base/module/base-module'
import { AssetsService, TechnologiesService } from '@core/services/api-gateway'

type TSortKeys = 'name'
type TSort = IDataSortState<keyof Pick<TTechnology, TSortKeys>>

export interface ITechnologiesState extends IDataSearch, TSort, IRemoteData {
    addingTechnologiesProgress: boolean
    data: TTechnology[]
}

const state = {
    search: '',
    loading: false,
    addingTechnologiesProgress: false,
    error: false,
    lastFetched: '',
    data: [],
    sort: {
        direction: 'asc',
        sortBy: 'name',
    },
} satisfies ITechnologiesState

class Technologies extends BaseModule<ITechnologiesState> {
    constructor(state: ITechnologiesState) {
        super(state, 'technologies.module')
    }

    search(searchTerm: string) {
        this.store.setKey('search', searchTerm)
    }

    getData(): TTechnology[] {
        const { data, search } = this.useStore()
        const options = {
            keys: ['name'],
        }
        const { results } = useFuzzy(data, search, options)
        const filteredData = search ? results : data

        const uniqueData = [
            ...new Map(filteredData.map((item) => [item.id, item])).values(),
        ]

        return uniqueData
    }

    async loadTechnologies(forced?: boolean): Promise<TTechnology[] | boolean> {
        const { store } = this

        if (!store.get().lastFetched || forced) {
            store.setKey('loading', true)
            const lastFetched = Date.now()
            const result = await AssetsService.getTechnologies()

            if (!result) {
                store.setKey('loading', false)
                store.setKey('error', true)
                return false
            }
            const technologies = (result as unknown as TTechnology[]) ?? []
            store.setKey('data', technologies)
            store.setKey('lastFetched', lastFetched)
            store.setKey('loading', false)
            return technologies
        }
        return store.get().data
    }

    async pollForTechnologies() {
        const jobs = await TechnologiesService.getAccountJobs()
        const { lastFetched } = this.store.get()
        const fetchTime = new Date()
        fetchTime.setDate(fetchTime.getDate() - 7)

        // Get job tracking ids for jobs that are from SCAN or USER_INPUT data feed source
        const jobTrackingIds = jobs
            .filter(
                (job: JobsResponseDataJob) =>
                    [DataFeedSource.SCAN, DataFeedSource.USER_INPUT].includes(
                        job.source
                    ) && job.created_at > fetchTime.toISOString()
            )
            .map((job: JobsResponseDataJob) => job.job_tracking_id)

        if (!jobTrackingIds.length) {
            return
        }

        const [statuses, latestEndTime] =
            await TechnologiesService.getTechnologiesAddedStatus(jobTrackingIds)

        // If the technologies weren't fetched yet or the latest job has a success status
        if (
            !lastFetched ||
            (latestEndTime &&
                statuses.includes(EndState.SUCCESS) &&
                latestEndTime > new Date(lastFetched))
        ) {
            await this.loadTechnologies(true)
        }
    }

    findTechnologyByName(name: string | undefined): TTechnology | undefined {
        if (!name) return undefined
        const { data } = this.store.get()
        const target = name.toLowerCase()
        return data.find((tech) => tech.name?.toLowerCase() === target)
    }

    async getAllTechnologies() {
        const result = await TechnologiesService.getAllTechnologies()
        if (!result) {
            return []
        }
        return [...result]?.sort(
            (a, b) => a.name?.localeCompare(b.name || '') || 0
        ) as TSlimTechnology[]
    }

    async addTechnologies(technologies: TTechnologyOption[]) {
        const { store } = this

        store.setKey('loading', true)
        store.setKey('addingTechnologiesProgress', true)
        const technologiesToAdd = technologies.map((tech) => {
            return {
                id: tech.key,
            }
        })

        const result =
            await TechnologiesService.addTechnologies(technologiesToAdd)
        if (result) {
            const [[status]] =
                await TechnologiesService.getTechnologiesAddedStatus([result])
            if (status === EndState.SUCCESS) {
                this.loadTechnologies(true)
                store.setKey('loading', false)
                store.setKey('addingTechnologiesProgress', false)
                return true
            }
        }
        store.setKey('loading', false)
        store.setKey('addingTechnologiesProgress', false)

        return false
    }
}

export const TechnologiesModule = new Technologies(state)
